<script setup lang="ts">
  import { defineAsyncComponent, computed, ref, watch, type Component } from "vue";
  import { pascalCase } from "change-case";
  import { Skeleton } from "@/components/ui/skeleton";
  import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

  interface Field {
    name: string;
    label?: string;
    accordion?: string;
    [key: string]: unknown;
  }

  interface CardItem {
    token: string;
    type?: string;
    [key: string]: unknown;
  }

  interface Props {
    fields: Field[];
    currentCard: CardItem;
  }

  const props = defineProps<Props>();

  const hasAccordion = computed(() => props.fields.some((f) => f.accordion));
  const openSections = ref<string[]>([]);

  watch(
    () => props.currentCard.token,
    () => {
      openSections.value = props.fields
        .filter((f) => f.is_accordion_open && f.accordion)
        .map((f) => f.accordion as string);
    },
    { immediate: true },
  );

  const determineComponent = ({ name }: Field): Component =>
    defineAsyncComponent(() => import(`./Fields/${pascalCase(name)}Field.vue`));
</script>

<template>
  <div class="-m-1 flex-1 overflow-y-auto [&_[data-slot=label]]:heading-label">
    <div class="space-y-6 p-1">
      <!-- Accordion rendering for fields with accordion property -->
      <template v-if="hasAccordion">
        <Accordion
          v-model="openSections"
          type="multiple"
          class="space-y-2">
          <template
            v-for="(field, index) in fields"
            :key="`current-card-body-field-${field.name}`">
            <AccordionItem
              v-if="field.accordion"
              :value="field.accordion"
              class="rounded-lg border px-3.5">
              <AccordionTrigger class="py-3 hover:no-underline">
                <div class="flex items-center gap-3">
                  <span class="text-13 font-semibold text-primary">{{ index + 1 }}. {{ field.label }}</span>
                  <span :id="`accordion-trigger-${field.accordion}`" />
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <Suspense>
                  <template #default>
                    <component
                      :is="determineComponent(field)"
                      :current-card="currentCard"
                      v-bind="field" />
                  </template>
                  <template #fallback>
                    <div class="flex items-center justify-center py-4">
                      <Skeleton class="h-8 w-full" />
                    </div>
                  </template>
                </Suspense>
              </AccordionContent>
            </AccordionItem>

            <div v-else>
              <Suspense>
                <template #default>
                  <component
                    :is="determineComponent(field)"
                    :current-card="currentCard"
                    v-bind="field" />
                </template>
                <template #fallback>
                  <div class="flex items-center justify-center py-4">
                    <Skeleton class="h-8 w-full" />
                  </div>
                </template>
              </Suspense>
            </div>
          </template>
        </Accordion>
      </template>

      <!-- Standard field rendering for card types without accordion -->
      <template v-else>
        <div
          v-for="field in fields"
          :key="`current-card-body-field-${field.name}`">
          <Suspense>
            <template #default>
              <component
                :is="determineComponent(field)"
                :current-card="currentCard"
                v-bind="field" />
            </template>
            <template #fallback>
              <div class="flex items-center justify-center py-4">
                <Skeleton class="h-8 w-full" />
              </div>
            </template>
          </Suspense>
        </div>
      </template>
    </div>
  </div>
</template>
